package dev.terrakok.mapitan.domain

import androidx.compose.runtime.Immutable
import androidx.compose.ui.graphics.Color
import kotlinx.serialization.Serializable
import org.maplibre.spatialk.geojson.Point

@Immutable
@Serializable
sealed interface DocumentObject {
    val id: String
}

@Immutable
@Serializable
data class Pin(
    override val id: String,
    @Serializable(with = PointSerializer::class)
    val point: Point,
    @Serializable(with = ColorSerializer::class)
    val color: Color
) : DocumentObject

@Immutable
@Serializable
data class TrackPoint(
    val id: String,
    @Serializable(with = PointSerializer::class)
    val point: Point,
)

@Serializable
enum class LineStyle(val dashArray: List<Number>?) {
    Solid(null),
    Dashed(listOf(2, 2)),
    Dotted(listOf(0.1, 2))
}

@Immutable
@Serializable
data class Track(
    override val id: String,
    @Serializable(with = TrackPointsDeltaSerializer::class)
    val points: List<TrackPoint>,
    @Serializable(with = ColorSerializer::class)
    val color: Color,
    val lineThickness: Int,
    val lineStyle: LineStyle,
) : DocumentObject

@Immutable
@Serializable
data class Area(
    override val id: String,
    @Serializable(with = TrackPointsDeltaSerializer::class)
    val points: List<TrackPoint>,
    @Serializable(with = ColorSerializer::class)
    val color: Color,
    val lineThickness: Int,
    val lineStyle: LineStyle,
) : DocumentObject

@Immutable
@Serializable
data class Label(
    override val id: String,
    @Serializable(with = PointSerializer::class)
    val point: Point,
    @Serializable(with = ColorSerializer::class)
    val color: Color,
    val text: String,
) : DocumentObject {
    companion object {
        const val DEFAULT_TEXT = "Text..."
        const val MAX_TEXT_LENGTH = 30
        const val MAX_TEXT_LINES = 3

        fun limitText(text: String): String {
            var lineBreaks = 0
            return buildString {
                text
                    .replace("\r\n", "\n")
                    .replace('\r', '\n')
                    .forEach { character ->
                        if (length == MAX_TEXT_LENGTH) return@buildString
                        if (character != '\n' || lineBreaks < MAX_TEXT_LINES - 1) {
                            append(character)
                            if (character == '\n') lineBreaks++
                        }
                    }
            }
        }
    }

    val displayText: String
        get() = text.ifBlank { DEFAULT_TEXT }
}

@Immutable
@Serializable
data class Document(
    val pins: List<Pin> = emptyList(),
    val tracks: List<Track> = emptyList(),
    val areas: List<Area> = emptyList(),
    val labels: List<Label> = emptyList(),
) {
    fun findById(id: String): DocumentObject? =
        pins.firstOrNull { it.id == id }
            ?: tracks.firstOrNull { it.id == id }
            ?: areas.firstOrNull { it.id == id }
            ?: labels.firstOrNull { it.id == id }
}